import { useState, useEffect, useContext } from 'react'
import { AuthContext } from '../contexts/AuthContext'
import './Drops.css'

const API = process.env.REACT_APP_API || ''

const apiFetch = async (path, opts = {}) => {
    const res = await fetch(API + path, {
        credentials: 'include',
        headers: { 'Content-Type': 'application/json', ...(opts.headers || {}) },
        ...opts,
    })
    const data = await res.json().catch(() => ({}))
    if (!res.ok) throw new Error(data.message || `HTTP ${res.status}`)
    return data
}

function getDropStatus(product) {
    const now = new Date()
    const { startsAt, endsAt } = product.dropConfig || {}
    if (!startsAt) return 'upcoming'
    if (new Date(startsAt) > now) return 'upcoming'
    if (endsAt && new Date(endsAt) < now) return 'ended'
    return 'live'
}

function formatCountdown(targetDate) {
    const diff = new Date(targetDate) - new Date()
    if (diff <= 0) return null
    const d = Math.floor(diff / 86400000)
    const h = Math.floor((diff % 86400000) / 3600000)
    const m = Math.floor((diff % 3600000) / 60000)
    if (d > 0) return `${d}j ${h}h ${m}min`
    if (h > 0) return `${h}h ${m}min`
    return `${m}min`
}

const STATUS_LABELS = { upcoming: 'À venir', live: 'En cours', ended: 'Terminé' }

const Drops = () => {
    const { user } = useContext(AuthContext)
    const brandId = user?.brand

    const [drops, setDrops] = useState([])
    const [loading, setLoading] = useState(true)
    const [now, setNow] = useState(new Date())

    useEffect(() => {
        const timer = setInterval(() => setNow(new Date()), 30000)
        return () => clearInterval(timer)
    }, [])

    const loadDrops = async () => {
        if (!brandId) return
        setLoading(true)
        try {
            const data = await apiFetch(`/brand/${brandId}/products?saleMode=drop`)
            setDrops(data.products || [])
        } catch { setDrops([]) }
        finally { setLoading(false) }
    }

    useEffect(() => { loadDrops() }, [brandId])

    if (loading) return <div className="drops-loading">Chargement...</div>

    const upcoming = drops.filter(p => getDropStatus(p) === 'upcoming')
    const live     = drops.filter(p => getDropStatus(p) === 'live')
    const ended    = drops.filter(p => getDropStatus(p) === 'ended')

    const renderDrop = (p) => {
        const status = getDropStatus(p)
        const { startsAt, endsAt } = p.dropConfig || {}
        const countdown = status === 'upcoming' && startsAt
            ? formatCountdown(startsAt)
            : status === 'live' && endsAt
                ? formatCountdown(endsAt)
                : null

        return (
            <div key={p._id} className={`drops-card drops-card--${status}`}>
                <div className="drops-card-header">
                    <span className="drops-card-name">{p.name}</span>
                    <span className={`drops-status drops-status--${status}`}>{STATUS_LABELS[status]}</span>
                </div>
                <div className="drops-card-meta">
                    {startsAt && (
                        <span>Début : {new Date(startsAt).toLocaleString('fr-FR')}</span>
                    )}
                    {endsAt && (
                        <span>Fin : {new Date(endsAt).toLocaleString('fr-FR')}</span>
                    )}
                </div>
                {countdown && (
                    <div className={`drops-countdown drops-countdown--${status}`}>
                        {status === 'upcoming' ? 'Démarre dans' : 'Se termine dans'}
                        <strong> {countdown}</strong>
                    </div>
                )}
                <div className="drops-card-footer">
                    <span className="drops-variants-count">
                        {p.variants?.length ?? 0} variant{p.variants?.length !== 1 ? 's' : ''}
                    </span>
                    <span className="drops-price">{(p.price / 100).toFixed(2)} €</span>
                </div>
            </div>
        )
    }

    return (
        <div className="drops-page">
            <div className="drops-header">
                <h2>Drops</h2>
                <p className="drops-hint">
                    Créez un drop depuis la section <strong>Inventaire</strong> en choisissant le mode de vente "Drop".
                </p>
            </div>

            {drops.length === 0 && (
                <p className="drops-empty">Aucun drop configuré. Créez un produit avec le mode de vente "Drop" depuis l'Inventaire.</p>
            )}

            {live.length > 0 && (
                <section className="drops-section">
                    <h3>En cours ({live.length})</h3>
                    <div className="drops-grid">{live.map(renderDrop)}</div>
                </section>
            )}

            {upcoming.length > 0 && (
                <section className="drops-section">
                    <h3>À venir ({upcoming.length})</h3>
                    <div className="drops-grid">{upcoming.map(renderDrop)}</div>
                </section>
            )}

            {ended.length > 0 && (
                <section className="drops-section">
                    <h3>Terminés ({ended.length})</h3>
                    <div className="drops-grid">{ended.map(renderDrop)}</div>
                </section>
            )}
        </div>
    )
}

export default Drops
